import { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, Anchor, Ship, Users2 } from "lucide-react";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

const KIND_META = {
  principal: { label: "Principals", icon: Anchor, placeholder: "e.g. Anglo Eastern" },
  vessel_type: { label: "Vessel Types", icon: Ship, placeholder: "e.g. Chemical Tanker" },
  rank: { label: "Ranks", icon: Users2, placeholder: "e.g. 3rd Officer" },
};

function MasterList({ kind, canManage, isSuper }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const canAdd = canManage && (kind === "rank" || (kind === "principal" && isSuper));
  const meta = KIND_META[kind];

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/master-data", { params: { kind } });
      setItems(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/master-data", { kind, value });
      setValue("");
      toast.success("Added");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const setEmail = async (item) => {
    const email = window.prompt(`Email address for ${item.value} (leave blank to clear)`, item.email || "");
    if (email === null) return;
    try {
      await api.patch(`/master-data/${item.id}/email`, { email });
      toast.success("Principal email saved");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove “${item.value}”?`)) return;
    try {
      await api.delete(`/master-data/${item.id}`);
      toast.success("Removed");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const Icon = meta.icon;
  return (
    <div className="bg-white rounded-xl border border-slate-200">
      <div className="p-5 border-b border-slate-100 flex items-center gap-3">
        <div className="w-9 h-9 rounded-md bg-cyan-50 text-cyan-700 flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-800">{meta.label}</div>
          <div className="text-xs text-slate-500">{items.length} total{kind === "vessel_type" && " · fixed list, used across the app"}{kind === "rank" && " · used across the app; new ranks are added before Others"}</div>
        </div>
      </div>

      {canAdd && (
        <form onSubmit={add} className="p-5 flex gap-2 border-b border-slate-100">
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={meta.placeholder}
            required
            data-testid={`master-${kind}-input`}
          />
          <Button type="submit" disabled={saving} className="bg-[#0b192c] hover:bg-[#0f2544]" data-testid={`master-${kind}-add-btn`}>
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add</>}
          </Button>
        </form>
      )}

      <div className="p-3">
        {loading && <div className="py-10 text-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400 inline" /></div>}
        {!loading && items.length === 0 && <div className="py-10 text-center text-sm text-slate-400">No entries yet</div>}
        <div className="flex flex-wrap gap-2 p-2" data-testid={`master-${kind}-list`}>
          {items.map((item) => (
            <div key={item.id} className="group flex items-center gap-2 px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-full text-sm text-slate-700">
              {item.value}
              {kind === "principal" && (
                <button type="button" disabled={!canManage || !isSuper} onClick={() => setEmail(item)} className={`text-xs ${item.email ? "text-cyan-700" : "text-slate-400"} ${canManage && isSuper ? "hover:underline" : ""}`} data-testid={`master-principal-email-${item.id}`}>
                  {item.email || (canManage && isSuper ? "+ email" : "no email")}
                </button>
              )}
              {canManage && isSuper && !item.fixed && (
                <button
                  onClick={() => remove(item)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-rose-600 hover:text-rose-700"
                  data-testid={`master-delete-${item.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function MasterDataPage() {
  const { hasPermission, user } = useAuth();
  const canManage = hasPermission("master_data.manage");
  const isSuper = ["Super Admin", "Management/HOD"].includes(user?.role?.role_name);

  return (
    <div className="space-y-6" data-testid="master-data-page">
      <div>
        <div className="text-xs uppercase tracking-widest text-slate-400">Reference data</div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight mt-1" style={{ fontFamily: "Manrope" }}>
          Master Data
        </h1>
        <div className="text-sm text-slate-500 mt-1">
          Principals can be added here by Super Admin or Management. Vessel types are a fixed list; new ranks can be added by Super Admin, Management and Manager/Planner Sourcing.
        </div>
      </div>

      <Tabs defaultValue="principal">
        <TabsList>
          <TabsTrigger value="principal" data-testid="master-data-tab-principals">Principals</TabsTrigger>
          <TabsTrigger value="vessel_type" data-testid="master-data-tab-vessel-types">Vessel Types</TabsTrigger>
          <TabsTrigger value="rank" data-testid="master-data-tab-ranks">Ranks</TabsTrigger>
        </TabsList>
        <TabsContent value="principal" className="mt-4">
          <MasterList kind="principal" canManage={canManage} isSuper={isSuper} />
        </TabsContent>
        <TabsContent value="vessel_type" className="mt-4">
          <MasterList kind="vessel_type" canManage={canManage} isSuper={isSuper} />
        </TabsContent>
        <TabsContent value="rank" className="mt-4">
          <MasterList kind="rank" canManage={canManage} isSuper={isSuper} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
